
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePropBotDetail } from '@/hooks/use-prop-bot-detail';
import { usePropBotMultilingualContent } from '@/hooks/use-prop-bot-multilingual-content';
import { useLanguage } from '@/contexts/LanguageContext';
import { EnhancedPropBotDetailHeader } from '@/components/prop-trading/detail/EnhancedPropBotDetailHeader';
import { EnhancedPropBotStats } from '@/components/prop-trading/detail/EnhancedPropBotStats';
import { EnhancedPropBotFeatures } from '@/components/prop-trading/detail/EnhancedPropBotFeatures';
import { EnhancedPropBotRequirements } from '@/components/prop-trading/detail/EnhancedPropBotRequirements';
import { EnhancedPropBotRules } from '@/components/prop-trading/detail/EnhancedPropBotRules';
import { Button } from '@/components/ui/button';
import { StartChallengeDialog } from '@/components/prop-trading/detail/StartChallengeDialog';
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usePropBotAccounts } from '@/hooks/use-prop-bot-accounts';
import { PropConnectedAccountsSection } from '@/components/prop-trading/detail/accounts/PropConnectedAccountsSection';
import { PropMergedTradingHistoryTable } from '@/components/prop-trading/detail/signals/PropMergedTradingHistoryTable';
import { usePropMergedTradingHistory } from '@/hooks/use-prop-merged-trading-history';
import { useQueryClient } from '@tanstack/react-query';
import { usePropTradingAccountValidation } from '@/hooks/use-prop-trading-account-validation';
import { useInstantAccountValidation } from '@/hooks/use-instant-account-validation';
import { InstantAccountBadge } from '@/components/prop-trading/InstantAccountBadge';
import { useTranslation } from "react-i18next";
import { useIsMobile } from '@/hooks/use-mobile';
import { usePropBotGrowth } from '@/hooks/use-prop-bot-growth';
import { useRealtimePropBot } from '@/hooks/use-realtime-prop-bot';
import { PropBotGrowthChart } from '@/components/prop-trading/growth/PropBotGrowthChart';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const PropTradingBotDetailPage = () => {
  const { botId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showDialog, setShowDialog] = useState(false);
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  // Clear cache on mount to ensure fresh data
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['propBot', botId] });
  }, [botId, queryClient]);

  const { language } = useLanguage();
  const { data: bot, isLoading: isBotLoading, enhancedStats, isEnhancedStatsLoading } = usePropBotDetail(botId as string);
  const { 
    multilingualContent, 
    isLoading: isI18nLoading,
    isTranslated,
    isOriginal,
    isFallback,
    originalLanguage
  } = usePropBotMultilingualContent(bot, language);
  const { data: accounts, isLoading: isAccountsLoading } = usePropBotAccounts(botId as string);
  
  // Trading history from published analysis
  const { data: tradingHistoryData, isLoading: isTradingHistoryLoading, refetch: refetchTradingHistory } = usePropMergedTradingHistory(botId as string);

  // Growth chart data
  const { data: growthData, isLoading: isGrowthLoading } = usePropBotGrowth(
    botId as string,
    bot?.propDetails?.[0]?.win_percentage ?? 2.0,
    bot?.propDetails?.[0]?.loss_percentage ?? 1.5,
    Number(bot?.propDetails?.[0]?.min_capital) ?? 100000
  );

  // Enable realtime updates for growth chart and trading history
  useRealtimePropBot(botId as string);

  const { 
    canStartChallenge, 
    getErrorMessage, 
    currentCount, 
    maxAccounts 
  } = usePropTradingAccountValidation();
  
  const { isInstantAccountBot } = useInstantAccountValidation(botId);

  // Enhanced success handler with Promise.all for immediate data sync
  const handleAddAccountSuccess = () => {
    setShowDialog(false);
    
    // Enhanced refresh with concurrent query refresh for immediate updates
    queryClient.refetchQueries({ queryKey: ['propBotDetail', botId] });
    queryClient.refetchQueries({ queryKey: ['propBotAccounts', botId] });
  };

  const { t } = useTranslation();

  if (isBotLoading) {
    return (
      <div className="container mx-auto animate-fade-in">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="h-48 bg-muted rounded-xl" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-64 bg-muted rounded-xl" />
            <div className="h-64 bg-muted rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!bot) {
    return (
      <div className="container mx-auto py-6">
        <p className="text-muted-foreground">{t('bot_not_found')}</p>
      </div>
    );
  }

  // Only show connected accounts section if accounts array exists and has items
  const hasConnectedAccounts = accounts && accounts.length > 0;

  const startChallengeButton = (
    <Button 
      onClick={() => setShowDialog(true)}
      className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/20 transition-all duration-300 hover:scale-105"
      disabled={!canStartChallenge()}
      size={isMobile ? "sm" : "default"}
    >
      {isMobile ? t('start') : t('start_challenge_with_count', { currentCount, maxCount: maxAccounts })}
    </Button>
  );

  return (
    <div className={`mx-auto space-y-${isMobile ? "6" : "8"} animate-fade-in`}>
      {/* Header section with enhanced background */}
      <div className="relative">
        <div className={`absolute inset-0 ${
          isInstantAccountBot 
            ? 'bg-gradient-to-r from-amber-500/10 via-yellow-500/5 to-orange-500/10' 
            : 'bg-gradient-to-r from-primary/5 via-transparent to-blue-500/5'
        } rounded-2xl blur-3xl`} />
        <div className="relative flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/prop-trading')}
              className={`${isMobile ? "mb-2 p-2" : "mb-4"} border border-input bg-background/50 backdrop-blur-sm hover:bg-background/80 transition-all duration-300 hover:scale-105 hover:shadow-md`}
              size={isMobile ? "icon" : "default"}
            >
              <ArrowLeft className={`${isMobile ? "h-4 w-4" : "mr-2 h-4 w-4"}`} />
              {!isMobile && t('back_to_prop_trading')}
            </Button>

            {/* Show Instant Account Badge for instant account bots */}
            {isInstantAccountBot && (
              <InstantAccountBadge className="animate-pulse" />
            )}

            {/* Only show Start Challenge button if there are no connected accounts */}
            {!hasConnectedAccounts && !isAccountsLoading && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {startChallengeButton}
                  </TooltipTrigger>
                  {!canStartChallenge() && (
                    <TooltipContent>
                      <p className={isMobile ? "text-xs" : ""}>{getErrorMessage()}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          <div className="animate-fade-in" style={{ animationDelay: '100ms' }}>
            <EnhancedPropBotDetailHeader 
              bot={bot} 
              mergedInstrument={growthData?.merged?.instrument}
              description={multilingualContent.description}
              isLoading={isI18nLoading}
              isTranslated={isTranslated}
              isOriginal={isOriginal}
              isFallback={isFallback}
              originalLanguage={originalLanguage}
            />
          </div>
        </div>
      </div>
      
      {/* Growth Chart Section - Only show when linked bot exists or has merged data */}
      {(growthData?.hasLinkedBot && (growthData?.originalBot?.growthData?.length > 0 || growthData?.shadowBot?.growthData?.length > 0)) && (
        <div className="animate-fade-in" style={{ animationDelay: '200ms' }}>
          <PropBotGrowthChart
            originalData={growthData?.originalBot?.growthData || []}
            shadowData={growthData?.shadowBot?.growthData || []}
            originalStats={growthData?.originalBot?.stats || { finalGrowth: 0, finalDollarAmount: 0, maxGrowth: 0, minGrowth: 0, maxDollarAmount: 0, minDollarAmount: 0, totalTrades: 0, wins: 0, losses: 0, winRate: 0, totalRange: 0, maxDrawdown: 0, maxDrawdownDollar: 0 }}
            shadowStats={growthData?.shadowBot?.stats || { finalGrowth: 0, finalDollarAmount: 0, maxGrowth: 0, minGrowth: 0, maxDollarAmount: 0, minDollarAmount: 0, totalTrades: 0, wins: 0, losses: 0, winRate: 0, totalRange: 0, maxDrawdown: 0, maxDrawdownDollar: 0 }}
            linkedBotType={growthData?.linkedBotType}
            minCapital={Number(bot?.propDetails?.[0]?.min_capital) ?? 100000}
            isSimulatedData={!growthData?.hasLinkedBot}
            isMerged={growthData?.hasMergedAnalysis}
            mergedData={growthData?.merged}
            currentBotType={bot?.type === 'shadow_bot' ? 'shadow' : 'original'}
          />
        </div>
      )}

      {/* Stats and Features section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="animate-fade-in" style={{ animationDelay: '300ms' }}>
          <EnhancedPropBotStats bot={bot} enhancedStats={enhancedStats} />
        </div>
        <div className="animate-fade-in" style={{ animationDelay: '400ms' }}>
          <EnhancedPropBotFeatures 
            bot={bot} 
            features={multilingualContent.features}
            isLoading={isI18nLoading}
          />
        </div>
      </div>

      {/* Requirements and Rules section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="animate-fade-in" style={{ animationDelay: '500ms' }}>
          <EnhancedPropBotRequirements 
            bot={bot} 
            requirements={multilingualContent.requirements}
            isLoading={isI18nLoading}
          />
        </div>
        <div className="animate-fade-in" style={{ animationDelay: '600ms' }}>
          <EnhancedPropBotRules 
            bot={bot} 
            challengeRules={multilingualContent.challenge_rules}
            isLoading={isI18nLoading}
          />
        </div>
      </div>

      {/* Connected accounts section */}
      {(hasConnectedAccounts || isAccountsLoading) && (
        <div className="animate-fade-in" style={{ animationDelay: '700ms' }}>
          <PropConnectedAccountsSection 
            accounts={accounts || []} 
            isLoading={isAccountsLoading}
            bot={bot}
          />
        </div>
      )}
      
      {/* Trading History - Always show */}
      <div className="animate-fade-in" style={{ animationDelay: '800ms' }}>
        <PropMergedTradingHistoryTable
          data={tradingHistoryData}
          isLoading={isTradingHistoryLoading}
          onRefresh={() => refetchTradingHistory()}
        />
      </div>

      <StartChallengeDialog
        bot={bot}
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
        onSuccess={handleAddAccountSuccess}
        defaultReversePosition={growthData?.linkedBotType === 'shadow'}
      />
    </div>
  );
};

export default PropTradingBotDetailPage;
